// composables/useEditorJsHtml.ts
import edjsHTML from 'editorjs-html'
import DOMPurify from 'dompurify'

export const useEditorJsHtml = () => {

  function rawParser(block:any){
    console.log(block)
    return `${block.data.html}`;
  }

  // Provide a parser for EditorJS Table blocks
  function tableParser(block: any){
    const data = block?.data || {}
    const withHeadings: boolean = !!data.withHeadings
    const rows: any[][] = Array.isArray(data.content) ? data.content : []

    if (!rows.length) return ''

    const escape = (s: any) => String(s ?? '')

    const [firstRow, ...rest] = rows
    const thead = withHeadings && Array.isArray(firstRow)
      ? `<thead><tr>${firstRow.map((c: any) => `<th>${escape(c)}</th>`).join('')}</tr></thead>`
      : ''

    const bodyRows = withHeadings ? rest : rows
    const tbody = bodyRows.length
      ? `<tbody>${bodyRows
          .map((r: any[]) => `<tr>${(Array.isArray(r) ? r : [])
            .map((c: any) => `<td>${escape(c)}</td>`).join('')}</tr>`)
          .join('')}</tbody>`
      : ''

    return `<table>${thead}${tbody}</table>`
  }

  const parser = edjsHTML({ raw: rawParser, table: tableParser })

  const toHtml = (data: any) => {
    try {
      // Editor.js Blocks -> HTML (array of strings)
      const htmlParts = parser.parse(data)
      const html = Array.isArray(htmlParts) ? htmlParts.join('') : String(htmlParts ?? '')
      // Sicherheit: sanitize!
      return DOMPurify.sanitize(html)
    }
    catch(e){
      return ''
    }
  }

  // Plain‑Text Parser für SEO Descriptions
  function decodeEntities(str: string): string {
    // Minimal HTML entity decoding for common entities
    return str
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
  }

  function stripHtml(str: string): string {
    return decodeEntities(String(str ?? '').replace(/<[^>]*>/g, ''))
  }

  function normalizeWhitespace(str: string): string {
    return str.replace(/\s+/g, ' ').trim()
  }

  const toText = (data: any, opts?: { maxLength?: number }): string => {
    try{
      const blocks = Array.isArray(data?.blocks) ? data.blocks : []
      const parts: string[] = []
      for (const block of blocks) {
        switch (block.type) {
          case 'paragraph':
            parts.push(stripHtml(block.data?.text || ''))
            break
          case 'header':
            parts.push(stripHtml(block.data?.text || ''))
            break
          case 'list': {
            const items: string[] = Array.isArray(block.data?.items) ? block.data.items : []
            const listText = items.map((it: any) => stripHtml(typeof it === 'string' ? it : it?.content || it)).join('; ')
            if (listText) parts.push(listText)
            break
          }
          case 'quote': {
            const q = stripHtml(block.data?.text || '')
            const c = stripHtml(block.data?.caption || '')
            parts.push(c ? `${q} — ${c}` : q)
            break
          }
          case 'table': {
            const rows: any[] = Array.isArray(block.data?.content) ? block.data.content : []
            const rowTexts = rows.map((row: any[]) => row?.map(cell => stripHtml(cell)).join(' - '))
            const tableText = rowTexts.filter(Boolean).join('; ')
            if (tableText) parts.push(tableText)
            break
          }
          case 'raw':
            parts.push(stripHtml(block.data?.html || ''))
            break
          case 'image': {
            const cap = stripHtml(block.data?.caption || '')
            if (cap) parts.push(cap)
            break
          }
          // delimiter and unknown types: skip
          default:
            break
        }
      }
      const text = normalizeWhitespace(parts.filter(Boolean).join(' '))
      const max = opts?.maxLength ?? text.length
      return text.length > max ? `${text.slice(0, max - 1).trim()}…` : text
    }
    catch(e){
      return ''
    }
  }

  return { toHtml, toText }
}

/*
204 * 4
274 * 4

*/
